/**
 * Privacy-hardened PostHog wrapper. All IDs hashed with sha256+salt; IP suppressed;
 * memory-only persistence (no localStorage); NO PII in any event.
 * If REACT_APP_POSTHOG_KEY is missing, every call is a silent no-op.
 */
import posthog from "posthog-js";

let initialized = false;

function hashId(id) {
  // Lightweight hash (sha256 via crypto.subtle if available; fallback to FNV)
  try {
    // Sync-ish fallback, we don't need crypto-strength for stable anonymous IDs.
    const s = String(id) + "aidwaive-2026-salt";
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = (h * 16777619) >>> 0; }
    return h.toString(16).padStart(8, "0");
  } catch { return "anon"; }
}

export function initAnalytics() {
  if (initialized) return;
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key) return;
  const host = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";
  try {
    posthog.init(key, {
      api_host: host,
      autocapture: false,                 // manual only
      capture_pageview: false,            // manual pageviews only
      capture_pageleave: false,
      disable_session_recording: true,
      persistence: "memory",              // no localStorage, more private
      loaded: (ph) => {
        ph.register({ "$ip": null });     // suppress IP
        // Anonymous-only: assign a stable ephemeral ID based on hash of a session-scoped random
        const anon = hashId(Math.random().toString(36) + Date.now());
        ph.identify("anon-" + anon);
        initialized = true;
      },
    });
    initialized = true;
  } catch { /* ignore */ }
}

export function pageview(path) {
  if (!initialized) return;
  try { posthog.capture("$pageview", { "$current_url": path }); } catch { /* noop */ }
}

const capture = (name, props) => {
  if (!initialized) return;
  try { posthog.capture(name, props || {}); } catch { /* noop */ }
};

// Only the events in the v4 spec. No PII. No field IDs. No financial values.
export const track = {
  landing_cta_click:       ()         => capture("landing_cta_click"),
  tool_opened:             (tool)     => capture("tool_opened", { tool }),
  chat_message_sent:       ()         => capture("chat_message_sent"),
  chat_suggestion_clicked: ()         => capture("chat_suggestion_clicked"),
  deadline_state_selected: ()         => capture("deadline_state_selected"),
  reminder_subscribe:      ()         => capture("reminder_subscribe"),
  reminder_confirm:        ()         => capture("reminder_confirm"),
  reminder_unsubscribe:    ()         => capture("reminder_unsubscribe"),
  estimator_calculated:    ()         => capture("estimator_calculated"),
  decoder_submitted:       ()         => capture("decoder_submitted"),
  compare_school_added:    ()         => capture("compare_school_added"),
  compare_summary_requested: ()       => capture("compare_summary_requested"),
  scholarship_clicked:     ()         => capture("scholarship_clicked"),
  scholarship_filtered:    ()         => capture("scholarship_filtered"),
  appeal_drafted:          ()         => capture("appeal_drafted"),
  loan_calculated:         ()         => capture("loan_calculated"),
  glossary_searched:       ()         => capture("glossary_searched"),
};
